class CartDrawerRecommendation extends HTMLElement {
	constructor() {
		super();
		this.swiperInstance = null;
		this.swiperEl = null;
		this.swiperWrapper = null;
	}

	connectedCallback() {
		this.swiperEl = this.querySelector('.swiper-cart-drawer-recommendation');
		if (!this.swiperEl) return;

		this.swiperWrapper = this.swiperEl.querySelector('.swiper-wrapper');
		if (!this.swiperWrapper) return;

		this.loadRecommendations();
	}

	async loadRecommendations() {
		try {
			const products = await this.getRecommendations();
			if (products && products.length > 0) {
				// console.log('products', products);
				this.renderProducts(products);
				// Wait for next frame to ensure DOM is updated, then initialize Swiper
				requestAnimationFrame(() => {
					this.initSwiper();
				});
			}
		} catch (error) {
			console.error('Error loading recommendations:', error);
		}
	}

	async getRecommendations() {
		const cartRes = await fetch('/cart.js');
		const cart = await cartRes.json();

		let productId;

		if (cart.items.length > 0) {
			productId = cart.items[0].product_id;
		} else {
			productId = 15699503579518; // fallback product
		}

		const recRes = await fetch(
			`/recommendations/products.json?product_id=${productId}&limit=6`
		);
		const data = await recRes.json();

		return data.products;
	}

	renderProducts(products) {
		if (!this.swiperWrapper) {
			console.error('Swiper wrapper not found');
			return;
		}

		// Clear any existing slides
		this.swiperWrapper.innerHTML = '';

		products.forEach((product) => {
			const slide = document.createElement('div');
			slide.className = 'swiper-slide';

			const productCard = this.createProductCard(product);
			slide.appendChild(productCard);
			this.swiperWrapper.appendChild(slide);
		});
	}

	createProductCard(product) {
		// console.log('createProductCard', product);
		const link = document.createElement('a');
		link.href = product.url;
		link.className = 'product-card';

		// Image wrapper
		const imageWrapper = document.createElement('div');
		imageWrapper.className = 'product-card__image-wrapper';

		if (product.featured_image) {
			const imageWrapperImage = document.createElement('div');
			imageWrapperImage.className = 'product-card__image-wrapper-image';

			const mainImage = document.createElement('img');
			mainImage.src = product.featured_image;
			mainImage.alt = product.title || '';
			mainImage.className = 'product-card__image';
			mainImage.loading = 'lazy';

			const hoverImage = document.createElement('img');
			hoverImage.src = (product.images && product.images.length > 1) ? product.images[1] : product.featured_image;
			hoverImage.alt = product.title || '';
			hoverImage.className = 'product-card__image image_on_hover';
			hoverImage.loading = 'lazy';

			imageWrapperImage.appendChild(mainImage);
			imageWrapperImage.appendChild(hoverImage);
			imageWrapper.appendChild(imageWrapperImage);
		}

		// Check if product is enquiry only
		if (product.tags && (product.tags.includes('enquiry') || product.tags.includes('Enquiry'))) {
			const enquiryBadge = document.createElement('div');
			enquiryBadge.className = 'product-card__enquiry-badge';

			const dot1 = document.createElement('span');
			dot1.className = 'product-card__enquiry-dot';
			const text = document.createElement('span');
			text.className = 'product-card__enquiry-text';
			text.textContent = 'Enquiry Only';
			const dot2 = document.createElement('span');
			dot2.className = 'product-card__enquiry-dot';

			enquiryBadge.appendChild(dot1);
			enquiryBadge.appendChild(text);
			enquiryBadge.appendChild(dot2);
			imageWrapper.appendChild(enquiryBadge);
		}

		link.appendChild(imageWrapper);

		// Content wrapper
		const content = document.createElement('div');
		content.className = 'product-card__content';

		const text = document.createElement('div');
		text.className = 'product-card__text';

		// Brand/Title
		const brand = document.createElement('div');
		brand.className = 'product-card__brand';
		brand.textContent = product.title ? product.title.toUpperCase() : '';

		// Price
		const price = document.createElement('div');
		price.className = 'product-card__price';
		const variant = product.variants && product.variants.length > 0 ? product.variants[0] : null;
		if (variant && variant.price) {
			price.textContent = this.formatMoney(variant.price);
		} else if (product.price) {
			price.textContent = this.formatMoney(product.price);
		}

		// Description
		const description = document.createElement('div');
		description.className = 'product-card__description two-lines-text';
		let descriptionText = '';

		// Try to get description from metafields first, then body_html, then description
		if (product.metafields && product.metafields.custom && product.metafields.custom.short_description) {
			descriptionText = product.metafields.custom.short_description.value || product.metafields.custom.short_description;
		} else if (product.body_html) {
			descriptionText = product.body_html;
		} else if (product.description) {
			descriptionText = product.description;
		}

		// Strip HTML tags and truncate to 20 words
		if (descriptionText) {
			// Remove HTML tags
			const tempDiv = document.createElement('div');
			tempDiv.innerHTML = descriptionText;
			let plainText = tempDiv.textContent || tempDiv.innerText || '';

			// Truncate to 20 words
			const words = plainText.trim().split(/\s+/);
			if (words.length > 20) {
				plainText = words.slice(0, 20).join(' ') + '...';
			}

			description.textContent = plainText;
		}

		text.appendChild(brand);
		text.appendChild(price);
		if (descriptionText) {
			text.appendChild(description);
		}
		content.appendChild(text);
		link.appendChild(content);

		return link;
	}

	formatMoney(cents) {
		// Try to use Shopify's money format if available
		if (window.Shopify?.formatMoney) {
			return window.Shopify.formatMoney(cents);
		}

		// Fallback: use Intl.NumberFormat
		const priceInDollars = cents / 100;
		const currency = window.Shopify?.currency?.active ||
						document.documentElement.getAttribute('data-currency') ||
						'GBP';
		return new Intl.NumberFormat('en-GB', {
			style: 'currency',
			currency: currency
		}).format(priceInDollars);
	}

	initSwiper() {
		// Check if Swiper is available using requestAnimationFrame loop
		if (typeof Swiper === 'undefined') {
			this.waitForSwiper();
			return;
		}

		this.checkAndInitSwiper();
	}

	waitForSwiper() {
		const checkSwiper = () => {
			if (typeof Swiper !== 'undefined') {
				this.checkAndInitSwiper();
			} else {
				// Check again on next frame
				requestAnimationFrame(checkSwiper);
			}
		};
		requestAnimationFrame(checkSwiper);
	}

	checkAndInitSwiper() {
		if (typeof Swiper === 'undefined') {
			console.error('Swiper library is not available');
			return;
		}

		if (!this.swiperEl) {
			console.error('Swiper element not found');
			return;
		}

		// Skip if already initialized
		if (this.swiperEl.swiper) {
			console.warn('Swiper already initialized');
			return;
		}

		// Check if there are slides
		const slides = this.swiperWrapper?.querySelectorAll('.swiper-slide');
		if (!slides || slides.length === 0) {
			console.warn('No slides found in swiper-wrapper');
			return;
		}

		const sectionId = this.getAttribute('data-section-id');
		const slidesPerViewDesktop = parseFloat(this.getAttribute('data-slides-per-view-desktop')) || 2.5;
		const slidesPerViewTablet = parseFloat(this.getAttribute('data-slides-per-view-tablet')) || 2.5;
		const slidesPerViewMobile = parseFloat(this.getAttribute('data-slides-per-view-mobile')) || 2.5;

		const config = {
			slidesPerView: slidesPerViewMobile,
			loop: false,
			breakpoints: {
				750: {
					slidesPerView: slidesPerViewTablet,
				},
				990: {
					slidesPerView: slidesPerViewDesktop,
				}
			}
		};

		const paginationEl = this.querySelector(`.swiper-cart-drawer-recommendation-pagination[data-section-id="${sectionId}"]`);
		if (paginationEl) {
			config.pagination = {
				el: paginationEl,
				clickable: true
			};
		}

		try {
			this.swiperInstance = new Swiper(this.swiperEl, config);
			// console.log('Swiper initialized successfully with', slides.length, 'slides');
		} catch (error) {
			console.error('Error initializing Swiper:', error);
		}
	}

	disconnectedCallback() {
		if (this.swiperInstance) {
			this.swiperInstance.destroy(true, true);
			this.swiperInstance = null;
		}
	}
}

if (!customElements.get('cart-drawer-recommendation')) {
	customElements.define('cart-drawer-recommendation', CartDrawerRecommendation);
}

